import { useEffect, useState } from "react";
import { useT } from "../../i18n";
import { FehlerBand } from "../../bausteine";
import { useKataloge, useKundennotizAnlegen, useNotizartAnlegen } from "../../daten/haken";
import { KatalogAnlegen } from "../../bausteine/KatalogAnlegen";
import { useRuf } from "../../schale/Ruf";
import type { Kunde } from "../../daten/typen";
import { Blattvonunten } from "../../bausteine/Blattvonunten";
import { Chipreihe, Zeilenblatt, Zweizeiler } from "../../bausteine/Handyformen";
import { Zeichen } from "../../bausteine/Zeichen";
import { heuteTag, tagPlus } from "../../daten/nachschlagen";
import { Kundensuche } from "./Kundensuche";
import { Tagesblatt, tagMitWochentag } from "./Zeitraumblatt";
import { SCHNELLWAHL, fruehesterTag, tageBis } from "../../daten/wiedervorlagefrist";
import "../../stile/handy-vertrieb.css";
import "../../stile/notizblatt.css";
import { heuteImBetrieb, wochentagVon } from "../../daten/betriebstag";

/* ══════════════════════════════════════════════════════════════════
   Die Notiz nach dem Besuch.

   Interview H2: *„Nehmen Sie den letzten Kundenbesuch: Was ist danach
   mit dem Ergebnis passiert?"* — und 06-crm.md zieht daraus den Schluss:
   *„Der Weg vom Gespräch zur festgehaltenen Absprache muss unter
   dreißig Sekunden liegen, sonst passiert er nicht."*

   Deshalb steht hier genau so viel: Datum und Kunde sind vorbelegt, und
   ein Knopf sichert. Den **Betreff** aus 06-crm.md fragt der Weg am
   Telefon **nicht** — er kostet einen Griff und sagt im Auto nichts, was
   der Satz nicht selbst sagt (Regel 11: Jedes Pflichtfeld braucht einen
   Grund).

   ── Der Anlass steht dabei (18.09.2026) ───────────────────────────

   Luca: *„Wenn wir den Vorgang einem Kunden erfassen, möchte ich hier auch
   sagen können, was es war: Absprache oder irgendwas anderes."*

   **Er war schon da, nur nicht zu sehen.** `customer_note.kind` trägt seit
   Migration 0065 die vier Werte `vermerk`, `telefonat`, `absprache` und
   `ruecksprache`; `POST /kunden/:id/notizen` nimmt sie als `art` entgegen,
   und der Verlauf schreibt sie am Schreibtisch längst hin
   (`texte.art.*`). Das Blatt am Telefon schickte stur `vermerk`. Also
   keine Migration, keine neue Spalte — eine Reihe Knöpfe.

   **Und kein Pflichtfeld** (Regel 11): „Vermerk" steht vorgewählt da, wie
   es die Datenbank als Vorgabe führt. Wer nichts anfasst, schreibt, was er
   vorher auch geschrieben hat.

   ── Und am selben Abend: die Liste wird gepflegt (Migration 0146) ──

   Luca: *„Notizarten passen, will ich aber pflegen können."* Die vier Werte
   standen als CHECK im Schema; eine fünfte hätte eine Migration gebraucht.
   Sie stehen jetzt in `note_kind` und werden unter *Weiteres → Notizarten*
   gepflegt — diese Reihe holt sie aus dem Katalog und nicht mehr aus einer
   Liste in dieser Datei.

   ── Warum die Reihe am 18.09.2026 trotzdem nicht dastand ──────────

   Luca, beim Durchklicken: *„Es fehlt hier auch die Möglichkeit zu wählen,
   was für eine Art von Notiz das ist."* — und die Maske holte sie zu dem
   Zeitpunkt längst.

   **Es lag weder an der Maske noch am Blatt, sondern an der Datenbank:**
   `note_kind` gibt es in der Cloud noch nicht. Nachgesehen am 18.09.2026,
   der letzte gefahrene Stand dort ist `20260918220000` (0142) — die
   Migrationen 0143 bis **0146** stehen im Repo und sind nicht gefahren.
   `GET /stamm/kataloge` liest `note_kind` mit und bricht deshalb ab; die
   Antwort kommt gar nicht erst an, und `kataloge.data` bleibt leer. Es
   fehlten damit **alle** Kataloge und nicht nur diese Reihe.

   **Die Maske bleibt trotzdem richtig, wie sie ist** (Regel 11): Ohne
   Liste steht keine Reihe da, ohne Wahl gilt die Vorgabe der Datenbank,
   und die Notiz geht durch. Sobald 0146 gefahren ist, steht die Reihe da,
   ohne dass hier etwas geändert werden muss.

   ── Der Fokus springt nicht hinein ────────────────────────────────

   Luca: *„‚Notiz erfassen' ist auch eine tolle Sache, aber es soll auch
   nicht sofort den Fokus auf dem Textfeld haben, weil wir dann auch direkt
   den gleichen Effekt wie bei den Datepickern haben."* Also: Die Tastatur
   fährt hoch, bevor jemand das Blatt gesehen hat, und deckt zwei Drittel
   davon zu — samt Anlass und Wiedervorlage.

   Seit dem 18.09.2026 fokussiert **kein** Blatt mehr ein Feld beim Öffnen;
   den Fokus bekommt der Dialog selbst (`bausteine/Blattvonunten.tsx`).
   `data-kein-erstfokus` am Feld ist damit erledigt und steht nur noch da,
   bis der letzte Leser es nicht mehr sucht.

   **Diktiert wird mit der Tastatur des Geräts.** Beide großen Systeme
   haben das Mikrofon in der Bildschirmtastatur; ein eigenes Sprachfeature
   wäre eine zweite Aufnahme, eine Berechtigung und ein Dienst, der
   Sprache zu Text macht — für dieselbe Zeile Text.

   **Die Wiedervorlage hängt am selben Knopf** (Migration 0111). Zwei
   Knöpfe hießen im Auto: Einer davon wird nicht gedrückt.
   ══════════════════════════════════════════════════════════════════ */

/** „Dienstag, 16. September" — der Kopf der Notiz, der Tag in deutscher Zeit. */
export function heuteLang(t: ReturnType<typeof useT>): string {
  const wochentage = t.liste("kalender.wochentageLang");
  const monate = t.liste("kalender.monate");
  const heute = heuteImBetrieb(); // deutscher Tag (N8-02)
  const [, monat = 1, tag = 1] = heute.split("-").map(Number);
  const tagName = wochentage[wochentagVon(heute)] ?? "";
  return `${tagName}, ${tag}. ${monate[monat - 1] ?? ""}`;
}

/* Die Fristen aus dem Auftrag — und „keine". Seit dem 03.10.2026 mit
   „heute" vorn (M-05, `daten/wiedervorlagefrist.ts`). */

/** „In 7 Tagen · Sa 26.9." — die Frist als Satz, mit dem Tag, an dem sie fällig wird. */
function fristSatz(t: ReturnType<typeof useT>, tage: number | null): string {
  if (tage === null) return t("wiedervorlage.keine");
  const tag = tagMitWochentag(t, tagPlus(heuteTag(), tage));
  if (tage === 0) return t("wiedervorlage.heuteAm", { tag });
  return tage === 1
    ? t("wiedervorlage.morgenAm", { tag })
    : t("wiedervorlage.inTagenAm", { tage, tag });
}

/* ══════════════ Die Wiedervorlage als eigene Zeile — 19.09.2026 ══════════════

   Luca: *„Wenn wir die scrollen rechts, dann hast du keine 3 Tage, 7 Tage,
   14 Tage. Wenn du das Ding seitlich verschiebst, dann geht das in den
   Begriff rein. Das geht gar nicht. Mach da bitte so, dass die
   Wiedervorlage ein separater Punkt ist, mit einem Bottom Sheet, wo du
   auswählst, und dann, wenn du sagst ‚Benutzerdefiniertes Datum', dass
   dann ein Date Picker hochgeht."*

   **Eine Zeile, die sagt, was gilt** — „Wiedervorlage · In 7 Tagen · Sa
   26.9." —, und ein zweites Blatt mit der Wahl: keine, die drei Fristen
   mit ihrem Tag daneben, und „Datum wählen", das den Kalender öffnet
   (`Tagesblatt`, derselbe wie beim Zeitraum). Die Reihe mit dem klebenden
   Schild, unter dem die Knöpfe durchliefen, ist weg.

   **Nach außen bleiben es Tage** (`wiedervorlage_tage`, 0 bis 365): Ein
   gewähltes Datum wird hier in Tage ab heute umgerechnet. Die Route und
   die Datenbank ändern sich dafür nicht. */

function Wiedervorlageblatt({ offen, frist, schliessen, waehlen }: {
  offen: boolean;
  frist: number | null;
  schliessen: () => void;
  waehlen: (tage: number | null) => void;
}) {
  const t = useT();
  const [kalender, setzeKalender] = useState(false);
  useEffect(() => { if (!offen) setzeKalender(false); }, [offen]);
  const heute = heuteTag();
  const eigenes = frist !== null && !(SCHNELLWAHL as readonly number[]).includes(frist);
  const zeile = (tage: number | null, titel: string, unter?: string) => (
    <Zweizeiler
      key={tage ?? "keine"}
      beiKlick={() => waehlen(tage)}
      winkel={false}
      titel={titel}
      {...(unter ? { unter } : {})}
      rechts={frist === tage ? <Zeichen name="haken" /> : undefined}
    />
  );
  return (
    <>
      <Blattvonunten offen={offen} schliessen={schliessen} titel={t("wiedervorlage.titel")}>
        <Zeilenblatt>
          {zeile(null, t("wiedervorlage.keine"))}
          {SCHNELLWAHL.map((tage) => zeile(
            tage,
            tage === 0 ? t("wiedervorlage.heuteLang") : t("wiedervorlage.inTagenLang", { tage }),
            tagMitWochentag(t, tagPlus(heute, tage)),
          ))}
          <Zweizeiler
            beiKlick={() => setzeKalender(true)}
            titel={t("wiedervorlage.datumWaehlen")}
            unter={eigenes ? tagMitWochentag(t, tagPlus(heute, frist)) : t("wiedervorlage.datumWaehlenSatz")}
            rechts={eigenes ? <Zeichen name="haken" /> : undefined}
          />
        </Zeilenblatt>
      </Blattvonunten>
      {offen && kalender ? (
        <Tagesblatt
          titel={t("wiedervorlage.datumWaehlen")}
          tag={frist === null ? null : tagPlus(heute, frist)}
          ab={fruehesterTag(heute)}
          schliessen={() => setzeKalender(false)}
          waehlen={(iso) => {
            const tage = tageBis(heute, iso);
            setzeKalender(false);
            waehlen(tage);
          }}
        />
      ) : null}
    </>
  );
}

export function NotizBlatt({ offen, schliessen, kunde }: {
  offen: boolean;
  schliessen: () => void;
  /** Steht der Kunde fest, wird er nicht gefragt. Sonst sucht das Blatt ihn. */
  kunde?: { nr: string; name: string } | undefined;
}) {
  const t = useT();
  const ruf = useRuf();
  const schreiben = useKundennotizAnlegen();

  /**
   * Woraufhin die Notiz entsteht — **aus dem Katalog**, seit Migration 0146.
   *
   * Hier stand bis zum 18.09.2026 dieselbe Liste wie im Schema und in der
   * Route. Sie ist jetzt eine gepflegte Liste (`note_kind`, unter *Weiteres →
   * Notizarten*); wer eine fünfte Art anlegt, sieht sie hier, ohne dass jemand
   * eine Migration fährt.
   *
   * `system` steht nicht dabei: Das schreibt die Anwendung selbst, wenn sie ein
   * Feld ändert (der Riegel `customer_note_spur_nur_system` hält es fest). Die
   * API lässt die Zeile schon draußen — hier wird nur noch gefiltert, was
   * stillgelegt ist.
   */
  /* Erst, wenn das Blatt offen ist (A-02): Es hängt immer an „Heute", und
     `GET /stamm/kataloge` ist dem technischen Außendienst verschlossen. */
  const kataloge = useKataloge(offen);
  const arten = (kataloge.data?.notizarten ?? []).filter((a) => a.aktiv);
  const artAnlegen = useNotizartAnlegen();

  const [wen, setzeWen] = useState<{ nr: string; name: string } | null>(kunde ?? null);
  const [wort, setzeWort] = useState("");
  const [text, setzeText] = useState("");
  /**
   * `null` heißt „nichts angefasst" und ist **nicht dasselbe wie `vermerk`**.
   *
   * Die Art ist kein Pflichtfeld (Regel 11): Ohne Angabe setzt die Datenbank
   * ihre Vorgabe, und die Maske schickt dann gar kein `art`. Solange die Liste
   * noch unterwegs ist, steht also keine Reihe da — und das Blatt tut
   * trotzdem, was es vorher tat.
   */
  const [art, setzeArt] = useState<string | null>(null);
  const [frist, setzeFrist] = useState<number | null>(null);
  const [fristOffen, setzeFristOffen] = useState(false);

  /* Beim Öffnen fängt das Blatt von vorn an. Ein Feld, in dem noch die
     Notiz vom letzten Kunden steht, ist die Art von Fehler, die erst in
     der Akte auffällt. */
  useEffect(() => {
    if (!offen) return;
    setzeWen(kunde ?? null);
    setzeWort("");
    setzeText("");
    setzeArt(null);
    setzeFrist(null);
    setzeFristOffen(false);
    schreiben.reset();
    // `schreiben` ist bei jedem Bild dieselbe Instanz; es gehört nicht in
    // die Liste, sonst räumt der Haken sich selbst ständig auf.
  }, [offen, kunde]);

  const sichern = () => {
    if (!wen || text.trim() === "") return;
    schreiben.mutate(
      {
        nr: wen.nr,
        text: text.trim(),
        // Nichts angefasst heißt: nichts schicken. Die Vorgabe führt die Spalte.
        ...(art === null ? {} : { art }),
        ...(frist === null ? {} : { wiedervorlageTage: frist }),
      },
      {
        onSuccess: () => {
          ruf(frist === null ? "notizGesichert" : "notizMitWiedervorlage");
          schliessen();
        },
      },
    );
  };

  /* Kein Kunde gewählt: Das Blatt sucht ihn zuerst. Es ist derselbe
     Schritt wie im Angebot — eine Notiz ohne Kunden hätte keinen Ort. */
  if (!wen) {
    return (
      <Blattvonunten offen={offen} schliessen={schliessen} titel={t("handyNotiz.werTitel")}>
        <Kundensuche
          wort={wort}
          wortAendern={setzeWort}
          waehlen={(k: Kunde) => setzeWen({ nr: k.nr, name: k.name })}
          kopfzeile={t("handyKunden.zuletzt")}
        />
      </Blattvonunten>
    );
  }

  return (
    <Blattvonunten
      offen={offen}
      schliessen={schliessen}
      /* Ein angefangener Satz geht beim Wegziehen nicht still verloren. Die
         Wahl von Anlass und Frist allein zählt nicht — ohne Text gibt es
         nichts zu sichern. */
      schmutzig={text.trim() !== ""}
      /* **Ein Blatt von unten, obwohl ein Feld darin steht** (18.09.2026).
         Die Regel für Blätter macht aus einem Tippfeld sonst ein Vollbild
         (`bausteine/Blattvonunten.tsx`) — hier nicht. Luca: *„da auch ein
         kleines Bottom Sheet, nicht so ein großes wieder."*

         Der Grund ist derselbe wie beim Nachschlagen: Das Feld ist drei
         Zeilen hoch und nicht der halbe Schirm, und was über dem Blatt
         stehen bleibt — der Kunde, bei dem man gerade war —, ist die
         Auskunft darüber, wohin die Notiz geht. Geht die Tastatur auf,
         rückt das Blatt darüber (`--tastatur`, `stile/telefon.css`); mit
         drei Zeilen Feld und zwei Knopfreihen passt es auch dann. */
      form="unten"
      titel={t("handyNotiz.titel")}
      /* **Die Firmierung steht in der zweiten Zeile** (19.09.2026). Luca:
         *„dann hast du einmal Samstag, 19. September, und dann diesen
         Mittelpunkt und dann die GmbH. Mach bitte die Firmierung in die
         zweite Zeile."* Hinter dem Tag brach der Name mittendrin um, und
         „GmbH" stand allein da. Der Zeilenumbruch hält `notizblatt.css`
         (`white-space: pre-line` am Kopf dieses Blatts). */
      unter={`${heuteLang(t)}\n${wen.name}`}
      {...(kunde ? {} : { zurueck: () => setzeWen(null) })}
      fuss={
        <button
          type="button"
          className="handy-knopf"
          disabled={text.trim() === "" || schreiben.isPending}
          onClick={sichern}
        >
          {t(schreiben.isPending ? "handyNotiz.sichertGerade" : "handyNotiz.sichern")}
        </button>
      }
    >
      {/* **Der Anlass steht über dem Feld und nicht darunter.** Er sagt,
          was der Satz danach ist — und wer ihn danebenstehen lässt, hat
          „Vermerk" geschrieben wie bisher. Dieselbe Reihe wie die
          Wiedervorlage darunter: eine Zeile Knöpfe, kein eigener Teil mit
          Rahmen und Überschrift.

          **Das Wort hat zwei Herkünfte** (Migration 0146): Die vier
          mitgelieferten Arten tragen einen Schlüssel und werden übersetzt;
          eine Art, die der Betrieb angelegt hat, trägt ihr Wort selbst.

          Solange die Liste unterwegs ist, steht die Reihe nicht da. Ein Knopf,
          der gleich noch drei Nachbarn bekommt, springt beim Hinsehen — und
          gebraucht wird er nicht: Ohne Wahl gilt die Vorgabe. */}
      {arten.length ? (
        /* Seit dem 19.09.2026 die Chipreihe des Hauses und das Schild
           darüber — nicht mehr davor und klebend, mit den Knöpfen darunter
           durchlaufend (dieselbe Klage wie bei der Wiedervorlage). */
        <div className="hnz-anlass">
          <span className="hnz-schild">{t("handyNotiz.artSchild")}</span>
          <Chipreihe schild={t("handyNotiz.artSchild")} gewaehlt={art}>
            {arten.map((a) => (
              <button
                key={a.id}
                type="button"
                className="chip"
                aria-pressed={art === null ? a.vorgabe : art === a.code}
                onClick={() => setzeArt(a.code)}
              >
                {a.schluessel ? t(a.schluessel) : a.label}
              </button>
            ))}
          </Chipreihe>
        </div>
      ) : null}
      {/* Fehlt die Art, wird sie hier angelegt — ohne Stammdaten (F16). */}
      {kataloge.isSuccess ? (
        <KatalogAnlegen
          griff="notizartAnlegen.griff"
          platz="notizartAnlegen.platz"
          anlegen={(bezeichnung) => artAnlegen.mutateAsync({ bezeichnung })}
          nachher={setzeArt}
        />
      ) : null}

      {/* **Das Feld ohne Schild darüber.** Der Kopf des Blatts sagt schon
          „Notiz" und nennt Tag und Kunden; ein „WAS WAR" darüber wäre die
          dritte Zeile, die dasselbe sagt.

          **Drei Zeilen, nicht sieben** (18.09.2026). Wer im Auto eine
          Absprache festhält, schreibt einen Satz; sieben Zeilen waren
          150 px eines Blattes, das damit nicht mehr klein sein konnte.
          Es wächst mit, wer mehr schreibt (`stile/notizblatt.css`).

          Kein `autoFocus`: Die Tastatur fährt nicht hoch, bevor jemand das
          Blatt gesehen hat (Luca, 18.09.2026). */}
      <textarea
        className="hnz-feld"
        data-kein-erstfokus
        rows={3}
        value={text}
        autoCapitalize="sentences"
        aria-label={t("handyNotiz.feldSchild")}
        placeholder={t("handyNotiz.platz")}
        onChange={(e) => setzeText(e.target.value)}
      />

      {/* **Die Wiedervorlage ist eine eigene Zeile** (19.09.2026, siehe
          `Wiedervorlageblatt`). „Keine" bleibt die Vorgabe (Regel 11). */}
      <Zeilenblatt>
        <Zweizeiler
          beiKlick={() => setzeFristOffen(true)}
          punkt={frist === null ? "ruhig" : "gut"}
          titel={t("wiedervorlage.schild")}
          unter={fristSatz(t, frist)}
        />
      </Zeilenblatt>
      <Wiedervorlageblatt
        offen={offen && fristOffen}
        frist={frist}
        schliessen={() => setzeFristOffen(false)}
        waehlen={(tage) => { setzeFrist(tage); setzeFristOffen(false); }}
      />

      {schreiben.isError ? (
        <FehlerBand fehler={schreiben.error} />
      ) : null}
    </Blattvonunten>
  );
}
